import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Upload, MessageSquare, User, FileText } from "lucide-react";
import { usePortal } from "../PortalContext";
import type { Task, PortalConversation } from "../PortalContext";

const STATUS_STYLES: Record<string, string> = {
    "New":         "border-slate-200 text-slate-500 bg-slate-50",
    "In Progress": "border-blue-100 text-blue-600 bg-blue-50/50",
    "Completed":   "border-emerald-100 text-emerald-600 bg-emerald-50/50",
    "Cancelled":   "border-slate-200 text-slate-400 bg-slate-50",
    "Pending":     "border-amber-100 text-amber-600 bg-amber-50/50",
};

type FilterType = "All" | "Tasks" | "Messages";

function getTaskIcon(task: Task) {
    switch (task.type) {
        case "File Upload":        return <Upload className="h-6 w-6" />;
        case "Question":           return <MessageSquare className="h-6 w-6" />;
        case "Complete Profile":   return <User className="h-6 w-6" />;
        case "File Review":
        case "Application Review": return <FileText className="h-6 w-6" />;
        default:                   return <Clock className="h-6 w-6" />;
    }
}

function formatDate(iso: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    const itemDay = new Date(d);
    itemDay.setHours(0, 0, 0, 0);
    const todayDay = new Date(now);
    todayDay.setHours(0, 0, 0, 0);

    if (itemDay.getTime() === todayDay.getTime()) {
        return `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
    if (itemDay.getTime() === yesterday.getTime()) return "Yesterday";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function TaskCard({ task, onNavigate }: { task: Task; onNavigate: (task: Task) => void }) {
    const statusClass = STATUS_STYLES[task.status] ?? STATUS_STYLES["New"];
    const isInactive = task.status === "Completed" || task.status === "Cancelled";

    return (
        <Card
            onClick={() => onNavigate(task)}
            className={`border border-slate-100 shadow-sm transition-all group overflow-hidden hover:shadow-md cursor-pointer ${isInactive ? "opacity-50" : ""}`}
        >
            <div className="flex items-center p-4 gap-4">
                <div className="relative w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 bg-blue-50 text-blue-500">
                    {getTaskIcon(task)}
                    {!task.read && !isInactive && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-slate-900 group-hover:text-primary transition-colors leading-snug">
                        {task.subject}
                    </h4>
                    {task.application_name && (
                        <p className="text-xs text-slate-500 truncate mt-0.5">{task.application_name}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {task.created_at && (
                            <span className="text-xs text-slate-400 italic">{formatDate(task.created_at)}</span>
                        )}
                        <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${statusClass}`}>
                            {task.status}
                        </span>
                        <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border border-blue-100 text-blue-500 bg-blue-50">
                            Task
                        </span>
                    </div>
                </div>
            </div>
        </Card>
    );
}

function ConversationCard({ conv, onNavigate }: { conv: PortalConversation; onNavigate: (conv: PortalConversation) => void }) {
    const lastMsg = conv.messages.length > 0 ? conv.messages[conv.messages.length - 1] : null;
    const unread = conv.messages.filter((m) => !m.read && m.from_field !== "External").length;

    return (
        <Card
            onClick={() => onNavigate(conv)}
            className="border border-slate-100 shadow-sm transition-all group overflow-hidden hover:shadow-md cursor-pointer"
        >
            <div className="flex items-center p-4 gap-4">
                <div className="relative w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 bg-purple-50 text-purple-500">
                    <MessageSquare className="h-6 w-6" />
                    {unread > 0 && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-slate-900 group-hover:text-primary transition-colors leading-snug">
                        {conv.application_name || conv.subject || "Conversation"}
                    </h4>
                    {conv.application_number && (
                        <p className="text-xs text-slate-500 truncate mt-0.5">{conv.application_number}</p>
                    )}
                    {lastMsg && (
                        <p className="text-xs text-slate-400 truncate mt-0.5 italic">
                            {lastMsg.message || lastMsg.subject || ""}
                        </p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {conv.updated_at && (
                            <span className="text-xs text-slate-400 italic">{formatDate(conv.updated_at)}</span>
                        )}
                        <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border border-purple-100 text-purple-500 bg-purple-50">
                            Message
                        </span>
                        {conv.is_closed && (
                            <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border border-slate-200 text-slate-400 bg-slate-50">
                                Closed
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </Card>
    );
}

export default function TasksTab({ basePath = "/portal", conversationPath }: { basePath?: string; conversationPath?: string }) {
    const { tasks, tasksLoading, conversations, conversationsLoading, applications, refreshTasks } = usePortal();

    useEffect(() => {
        if (tasks.length === 0) {
            refreshTasks();
        }
    }, []);
    const navigate = useNavigate();

    function resolveNavId(conv: PortalConversation): string | null {
        const matched = applications.find(
            (a) => a.Application_External_ID === conv.application_external_id
        );
        return matched?.Application_Number ?? conv.application_number ?? null;
    }
    const [filter, setFilter] = useState<FilterType>("All");

    const filteredTasks = filter === "Messages" ? [] : tasks.filter((t) => !t.is_message);
    const filteredConvs = filter === "Tasks" ? [] : conversations;

    const pendingCount = tasks.filter((t) => !t.is_message && t.status !== "Completed" && t.status !== "Cancelled").length;
    const unreadConvCount = conversations.filter((c) => c.messages.some((m) => !m.read && m.from_field !== "External")).length;

    const FILTERS: FilterType[] = ["All", "Tasks", "Messages"];
    const isLoading = tasksLoading || conversationsLoading;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between px-2">
                <h3 className="text-xl font-serif font-black text-slate-900 tracking-tight">
                    My Tasks
                </h3>
                <div className="flex gap-2">
                    {pendingCount > 0 && (
                        <Badge
                            variant="outline"
                            className="bg-amber-50 text-amber-600 border-amber-100 font-bold uppercase tracking-widest text-[10px]"
                        >
                            {pendingCount} Pending
                        </Badge>
                    )}
                    {unreadConvCount > 0 && (
                        <Badge
                            variant="outline"
                            className="bg-purple-50 text-purple-600 border-purple-100 font-bold uppercase tracking-widest text-[10px]"
                        >
                            {unreadConvCount} Unread
                        </Badge>
                    )}
                </div>
            </div>

            <div className="flex gap-2 px-2">
                {FILTERS.map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-[10px] font-black uppercase tracking-widest px-4 rounded-full border transition-all ${
                            filter === f
                                ? "bg-primary text-white border-primary"
                                : "bg-white text-slate-500 border-slate-200 hover:border-primary hover:text-primary"
                        }`}
                    >
                        {f}
                    </button>
                ))}
            </div>

            {isLoading ? (
                <div className="flex flex-col gap-3">
                    {[1, 2, 3].map((num) => (
                        <Card key={num} className="border border-slate-100 shadow-sm rounded-2xl bg-white p-4 h-20 animate-pulse" />
                    ))}
                </div>
            ) : filteredTasks.length === 0 && filteredConvs.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-16 text-slate-400">
                    <Clock className="w-8 h-8" />
                    <p className="text-sm font-bold">No {filter === "All" ? "" : filter.toLowerCase()} found.</p>
                </div>
            ) : (
                <div className="grid gap-4">
                    {filteredTasks.map((task) => (
                        <TaskCard
                            key={task.task_id}
                            task={task}
                            onNavigate={(t) => navigate(`${basePath}/tasks/${t.task_id}`)}
                        />
                    ))}
                    {filteredConvs.map((conv) => (
                        <ConversationCard
                            key={conv.id}
                            conv={conv}
                            onNavigate={(c) => {
                                const navId = resolveNavId(c);
                                if (navId) navigate(conversationPath
                                    ? `${basePath}/${conversationPath}/${navId}`
                                    : `${basePath}/applications/${navId}/messages`
                                );
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
